import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  Stack,
  TextField,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
} from "@mui/material";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useCreatePackageMutation,
  useUpdatePackageMutation,
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  maxHeight: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function AddPackageModal({ editProduct, setEditProduct }) {
  const formRef = React.useRef();
  const fieldRefs = React.useRef({});

  const [addPackage, resultStats] = useCreatePackageMutation();

  const [updatePackage, updateResultStats] = useUpdatePackageMutation();

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const handleOpen = React.useCallback(() => setOpen(true), []);
  const handleClose = React.useCallback(() => setOpen(false), []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats, handleClose]);

  React.useEffect(() => {
    if (updateResultStats?.isLoading) {
      setLoad(true);
    }

    if (!updateResultStats?.isLoading) {
      setLoad(false);
    }

    if (updateResultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
      setEditProduct({});
    }

    if (updateResultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [updateResultStats, handleClose, setEditProduct]);

  const scrollToError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];

    if (firstErrorKey !== "variants") {
      if (fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      const firstVariantIndex = Object.keys(errors.variants)[0];

      if (firstVariantIndex !== undefined) {
        const firstFieldKey = Object.keys(
          errors.variants[firstVariantIndex]
        )[0];
        const refKey = `variants.[${firstVariantIndex}].${firstFieldKey}`;

        if (fieldRefs.current[refKey]) {
          fieldRefs.current[refKey].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };

  // Track previous open state to detect modal close transition
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    // Only clear editProduct when modal actually CLOSES (was open, now closed)
    if (prevOpenRef.current && !open && editProduct?._id) {
      setEditProduct({});
    }
    prevOpenRef.current = open;
  }, [open, editProduct?._id, setEditProduct]);

  React.useEffect(() => {
    // DEBUG: Log editProduct data when it changes (dev only)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AddPackageModal] 🔍 DEBUG - editProduct:', editProduct);
      console.log('[AddPackageModal] 🔍 DEBUG - editProduct._id:', editProduct?._id);
      console.log('[AddPackageModal] 🔍 DEBUG - editProduct.name:', editProduct?.name);
    }
    if (editProduct?._id) handleOpen();
  }, [editProduct?._id, handleOpen, editProduct]);

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Package has been created successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>
        Add Package
      </Button>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              name: editProduct?.name || "",
              iframeVisibility: editProduct?.iframeVisibility || false,
              productVisibility: editProduct?.productVisibility || false,
              allowMakeMagic: editProduct?.allowMakeMagic || false,
              allowManualItems: editProduct?.allowManualItems || false,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              let data;
              const {
                name,
                allowMakeMagic,
                allowManualItems,
                iframeVisibility,
                productVisibility,
              } = values;

              data = {
                name,
                allowMakeMagic,
                allowManualItems,
                iframeVisibility,
                productVisibility,
              };

              if (editProduct?._id)
                updatePackage({ id: editProduct?._id, data });
              else addPackage(data);
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setFieldValue,
              validateForm,
            }) => (
              <Form noValidate autoComplete="off">
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  variant="h6"
                  mb={2}
                >
                  {editProduct?._id ? "Update" : "Add"} Package
                </Typography>

                <br></br>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variant="outlined"
                    name="name"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    required
                    ref={(el) => (fieldRefs.current["name"] = el)}
                    helperText={
                      getIn(touched, "name") && getIn(errors, "name")
                        ? getIn(errors, "name")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "name") && getIn(errors, "name")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
                    <FormLabel component="legend">Visibility</FormLabel>
                    <RadioGroup
                      row
                      name="visibilityOption"
                      value={
                        values.iframeVisibility
                          ? "iframe"
                          : values.productVisibility
                          ? "product"
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setFieldValue("iframeVisibility", v === "iframe");
                        setFieldValue("productVisibility", v === "product");
                      }}
                    >
                      <FormControlLabel
                        value="iframe"
                        control={<Radio />}
                        label="Iframe Visible"
                      />
                      <FormControlLabel
                        value="product"
                        control={<Radio />}
                        label="Product Visible"
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack>

                <Stack my={2}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Allow Options</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.allowMakeMagic}
                            onChange={handleChange}
                            name="allowMakeMagic"
                          />
                        }
                        label="Allow Magic"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.allowManualItems}
                            onChange={handleChange}
                            name="allowManualItems"
                          />
                        }
                        label="Allow Manual Items"
                      />
                    </FormGroup>
                  </FormControl>
                </Stack>

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                    onClick={() => {
                      validateForm().then((errors) => {
                        if (Object.keys(errors).length > 0) {
                          scrollToError(errors);
                        }
                      });
                    }}
                  >
                    {editProduct?._id ? "Update" : "Add"}
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
